import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import find from 'lodash/find';

import Layout, { LayoutSection } from 'cirrus/Layout';
import { GatewayParameters } from 'pages/store/selectors/gateway';
import { Divider } from '@lightspeed/flame/Divider';
import Modules from 'shared_modules/Modules';

import GatewayParameter from './GatewayParameter';
import Swipers from './Swipers';
import Skeleton from './Skeleton';

const STRINGS = {
  SECURITY_OPTIONS: {
    TITLE: 'Security Options',
    LABEL: 'Allow Credits',
    DESCRIPTION: 'Allow refunds without original charge',
  },
  PARTIAL_APPROVALS: {
    TITLE: 'Partial Approvals',
    LABEL: 'Allow Partial Approvals',
    DESCRIPTION:
      'If activated, the terminal can accept an authorization response for an amount that is less than the amount requested.',
  },
  DUPLICATES: {
    TITLE: 'Duplicate Sales',
    LABEL: 'Prevent Duplicate Sales',
    DESCRIPTION:
      'If activated, two or more transactions for the same amount in a short period of time will not be allowed. It is intended to reduce the risk of charging a customer twice by mistake.',
  },
  SWIPERS_ENABLED: {
    TITLE: 'Swipers',
    DESCRIPTION: 'Activate support for non-EMV swipers',
  },
  MAGSTRIPE_FALLBACK: {
    TITLE: 'Magnetic Stripe Fallback',
    LABEL: 'Allow Magnetic Stripe Fallback',
    DESCRIPTION:
      "If activated and the card details can't be obtained from the card chip, the magnetic stripe reader is proposed as an alternative card entry method.",
  },
};

const GatewayConfiguration = ({ isLoading, parameters }) => {
  const allowCreditsParameter = find(
    parameters,
    parameter => parameter.id === GatewayParameters.ALLOW_CREDITS
  );
  const checkDuplicatesParameter = find(
    parameters,
    parameter => parameter.id === GatewayParameters.CHECK_DUPLICATES
  );
  const partialApprovalsParameter = find(
    parameters,
    parameter => parameter.id === GatewayParameters.PARTIAL_APPROVALS
  );
  const swipersEnableParameter = find(
    parameters,
    parameter => parameter.id === GatewayParameters.SWIPERS_ENABLED
  );
  const magstripeFallbackParameter = find(
    parameters,
    parameter => parameter.id === GatewayParameters.MAGSTRIPE_FALLBACK
  );
  const swipersFeatureEnabled = Modules.hasModule('payments.settings.allowswipers');

  return isLoading ? (
    <Layout>
      <LayoutSection title="" description="">
        <Skeleton />
      </LayoutSection>
    </Layout>
  ) : (
    <Fragment>
      {allowCreditsParameter && (
        <Fragment>
          <Divider />
          <LayoutSection
            key={allowCreditsParameter.id}
            title={STRINGS.SECURITY_OPTIONS.TITLE}
            description={STRINGS.SECURITY_OPTIONS.DESCRIPTION}
          >
            <GatewayParameter
              isLoading={isLoading}
              label={STRINGS.SECURITY_OPTIONS.LABEL}
              fieldName={allowCreditsParameter.id}
            />
          </LayoutSection>
        </Fragment>
      )}
      {partialApprovalsParameter && (
        <Fragment>
          <Divider />
          <LayoutSection
            key={partialApprovalsParameter.id}
            title={STRINGS.PARTIAL_APPROVALS.TITLE}
            description={STRINGS.PARTIAL_APPROVALS.DESCRIPTION}
          >
            <GatewayParameter
              isLoading={isLoading}
              label={STRINGS.PARTIAL_APPROVALS.LABEL}
              fieldName={partialApprovalsParameter.id}
            />
          </LayoutSection>
        </Fragment>
      )}
      {checkDuplicatesParameter && (
        <Fragment>
          <Divider />
          <LayoutSection
            key={checkDuplicatesParameter.id}
            title={STRINGS.DUPLICATES.TITLE}
            description={STRINGS.DUPLICATES.DESCRIPTION}
          >
            <GatewayParameter
              isLoading={isLoading}
              label={STRINGS.DUPLICATES.LABEL}
              fieldName={checkDuplicatesParameter.id}
            />
          </LayoutSection>
        </Fragment>
      )}
      {swipersEnableParameter && swipersFeatureEnabled && (
        <Fragment>
          <Divider />
          <LayoutSection
            key={swipersEnableParameter.id}
            title={STRINGS.SWIPERS_ENABLED.TITLE}
            description={STRINGS.SWIPERS_ENABLED.DESCRIPTION}
          >
            <Swipers isLoading={isLoading} fieldName={swipersEnableParameter.id} />
          </LayoutSection>
        </Fragment>
      )}
      {magstripeFallbackParameter && (
        <Fragment>
          <Divider />
          <LayoutSection
            key={magstripeFallbackParameter.id}
            title={STRINGS.MAGSTRIPE_FALLBACK.TITLE}
            description={STRINGS.MAGSTRIPE_FALLBACK.DESCRIPTION}
          >
            <GatewayParameter
              isLoading={isLoading}
              label={STRINGS.MAGSTRIPE_FALLBACK.LABEL}
              fieldName={magstripeFallbackParameter.id}
            />
          </LayoutSection>
        </Fragment>
      )}
    </Fragment>
  );
};

GatewayConfiguration.propTypes = {
  isLoading: PropTypes.bool,
  parameters: PropTypes.arrayOf(PropTypes.shape),
};

GatewayConfiguration.defaultProps = {
  isLoading: true,
  parameters: [],
};

export default GatewayConfiguration;
